import { useEffect, useState } from "react";
import tw from "tailwind-styled-components";
import { MdAdd, MdClose } from "react-icons/md";
import "react-datepicker/dist/react-datepicker.css";
import { capitalizeFirstLetter } from "../../helpers";
import moment from "moment";
import { FiChevronRight } from "react-icons/fi";
import { FaRegBuilding } from "react-icons/fa";
import Config from "../../Config";

const stageOptions = [
    { value: 1, label: "Inquiry" },
    { value: 2, label: "Visit Scheduled" },
    { value: 3, label: "Negotiation" },
    { value: 4, label: "Closed" },
    { value: 5, label: "Lost" },
];

const ViewDealsModal = ({
    handleClose,
    selectedData,
    apiData,
    handleAssignStaff,
    setIsAddDeal
}) => {
    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Helper function to get stage color
    const getStageColor = (stage) => {
        const colors = {
            'Inquiry': 'bg-blue-50 text-blue-700 border-blue-100',
            'Visit Scheduled': 'bg-yellow-50 text-yellow-700 border-yellow-100',
            'Negotiation': 'bg-purple-50 text-purple-700 border-purple-100',
            'Closed': 'bg-green-50 text-green-700 border-green-100',
            'Lost': 'bg-red-50 text-red-700 border-red-100',
        };
        return colors[stage] || 'bg-gray-50 text-gray-700 border-gray-100';
    };

    // Helper function to get follow-up status
    const getFollowupStatus = (followupDate) => {
        if (!followupDate) return null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const fDate = new Date(followupDate);
        fDate.setHours(0, 0, 0, 0);

        const diffDays = Math.ceil((fDate - today) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { label: 'Overdue', color: 'text-red-600' };
        } else if (diffDays === 0) {
            return { label: 'Today', color: 'text-orange-600 font-semibold' };
        } else if (diffDays <= 7) {
            return { label: `${diffDays} days left`, color: 'text-blue-600' };
        }
        return null;
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Header>
                <h1 className="text-white text-lg font-medium">Deals</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>

            <ProfileSection className="px-4 py-3 flex items-center justify-between">
                {/* Left: Icon + Info */}
                <div className="flex items-center gap-2">

                    {/* Inventory Icon */}
                    <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                        <FaRegBuilding className="text-gray-600" />
                    </div>

                    {/* Text Info */}
                    <div className="flex flex-col">
                        <p className="text-md font-medium text-gray-800">
                            {capitalizeFirstLetter(selectedData?.inventoryName || "")}
                        </p>
                        <p className="text-xs text-gray-400">
                            {selectedData?.address || ""}
                        </p>
                    </div>

                </div>

                {/* Right: Deals */}
                <div className="text-right">
                    <p className="text-xs text-gray-400">Deals</p>
                    <p className="text-lg font-bold text-green-600">
                        {apiData?.length || 0}
                    </p>
                </div>
            </ProfileSection>

            {/* Deals List */}
            <Content>
                {apiData?.length > 0 ? (
                    apiData.map((deal, index) => {
                        const statusName =
                            stageOptions.find((stage) => stage.value === deal.status)?.label;
                        const stageColor = getStageColor(statusName);
                        const followupStatus = getFollowupStatus(deal.nextFollowupDate);

                        return (
                            <DealCard key={deal.id || index}>
                                <CardHeader>
                                    <ClientInfo>
                                        <ClientName>
                                            {deal.name}
                                        </ClientName>
                                        <ClientMobile>
                                            {'#' + deal.mobile}
                                        </ClientMobile>
                                    </ClientInfo>
                                    {/* <StageBadge className={stageColor}>
                                        {statusName}
                                    </StageBadge> */}
                                    <div className="flex flex-col items-end gap-1">
                                        <StageBadge className={stageColor}>
                                            {statusName}
                                        </StageBadge>
                                    </div>
                                </CardHeader>

                                {/* <Divider /> */}

                                <ContentBox className="">
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-400">Budget</span>

                                        <div className="flex items-center gap-1">
                                            <span className="text-sm font-medium text-gray-700">
                                                {'₹' + deal.budget}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex flex-col text-right">
                                        <span className="text-xs text-gray-400">Visit/Follow Up</span>
                                        <span className="text-sm font-medium text-gray-700">
                                            {deal.followUpDate
                                                ? moment(deal.followUpDate).format("DD MMM, hh:mm A")
                                                : "Not Scheduled"}
                                        </span>
                                    </div>
                                </ContentBox>
                                <ContentBox className="">
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-400">Staff</span>

                                        <div className="flex items-center gap-1">
                                            {/* <span className="text-sm font-medium text-gray-700">
                                                {deal.assignedStaff || "Not Assigned"}
                                            </span>
                                            {!deal.assignedStaff && (
                                                <FiChevronRight className="text-gray-400 cursor-pointer" size={14} onClick={() => handleAssignStaff(deal)} />
                                            )} */}
                                            <span className="text-sm font-medium text-gray-700">
                                                {deal.assignedStaffs && deal.assignedStaffs.length > 0
                                                    ? deal.assignedStaffs.map((staff) => staff.name).join(", ")
                                                    : "Not Assigned"}
                                            </span>

                                            {(!deal.assignedStaffs || deal.assignedStaffs.length === 0) && (
                                                <FiChevronRight
                                                    className="text-gray-400 cursor-pointer"
                                                    size={14}
                                                    onClick={() => handleAssignStaff(deal)}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-col text-right">
                                        <span className="text-xs text-gray-400">Any Restriction</span>
                                        <span className="text-sm font-medium text-gray-700">
                                            {deal?.assignedStaffs?.[0]?.visitSlot || "No Restrictions"}
                                        </span>
                                    </div>
                                </ContentBox>
                                {Number(deal.status) === Config.DEAL_STATUS.BOOKED && (
                                    <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 mt-3 flex flex-col gap-2">
                                        <div className="flex justify-between items-center">
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-400">Deal Value</span>

                                                <div className="flex items-center gap-1">
                                                    <span className="text-sm font-medium text-gray-700">
                                                        {'₹' + (deal?.budget || 0)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex flex-col text-right">
                                                <span className="text-xs text-gray-400">Recvd Commission</span>
                                                <span className="text-sm font-medium text-gray-700">
                                                    {'₹' + (deal?.commissionRecieved || 0)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-400">Commission From Seller</span>

                                                <div className="flex items-center gap-1">
                                                    <span className="text-sm font-medium text-gray-700">
                                                        {'₹' + deal.sellerCommission || 0}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex flex-col text-right">
                                                <span className="text-xs text-gray-400">Commission From Buyer</span>
                                                <span className="text-sm font-medium text-gray-700">
                                                    {'₹' + deal.buyerCommission || 0}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {Number(deal.status) === Config.DEAL_STATUS.LOST && (
                                    <NoteSection>
                                        <Label>
                                            Lost Reason
                                        </Label>
                                        <NoteText>
                                            {deal.note}
                                        </NoteText>
                                    </NoteSection>
                                )}
                                {/* <DealDetails> */}
                                {Number(deal.status) !== Config.DEAL_STATUS.LOST && deal.note && (
                                    <NoteSection>
                                        <Label>
                                            Notes
                                        </Label>
                                        <NoteText>
                                            {deal.note}
                                        </NoteText>
                                    </NoteSection>
                                )}
                                {/* </DealDetails> */}

                                <Footer>
                                    {deal.updatedAt && (
                                        <span className="text-xs text-gray-400 ml-3">
                                            Last Updated: {moment(deal.updatedAt).format('DD MMM YY')}
                                        </span>
                                    )}
                                </Footer>
                            </DealCard>
                        );
                    })
                ) : (
                    <EmptyState>
                        <p className="text-gray-400">No deals available for this inventory</p>
                    </EmptyState>
                )}
            </Content>

            <FabButton
                onClick={() => setIsAddDeal(true)}
                aria-label="Add Deal"
            >
                <MdAdd size={24} />
            </FabButton>
        </Container>
    );
};

// Styled Components
const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-gradient-to-r from-primary-color to-teal-400 py-4 px-4`;
const ProfileSection = tw.div`flex items-center gap-3 bg-gray-100 px-4 p-4`;
const Summary = tw.div`flex items-center`;
const SummaryItem = tw.div`flex flex-col items-end`;
const Content = tw.div`px-4 py-4 space-y-4 overflow-y-auto flex-1`;
const DealCard = tw.div`bg-white border border-gray-200 rounded-lg p-4`;
const CardHeader = tw.div`flex justify-between items-start mb-3`;
const ClientInfo = tw.div`flex flex-col`;
const ClientName = tw.div`flex items-center text-base font-medium text-gray-800`;
const ClientMobile = tw.div`flex items-center text-sm text-gray-500 mt-1`;
const StageBadge = tw.span`text-xs px-3 py-1.5 rounded-full font-medium whitespace-nowrap`;
const Divider = tw.div`h-px bg-gray-100 my-3`;
const DealDetails = tw.div`space-y-3`;
const DetailRow = tw.div`grid grid-cols-2 gap-4`;
const DetailItem = tw.div`flex flex-col`;
const Label = tw.label`text-sm text-gray-400 font-normal`;
const Value = tw.span`text-gray-600 text-sm md:text-base`;
const NoteSection = tw.div`mt-2`;
const NoteText = tw.p`text-sm text-gray-600 bg-gray-50 p-2 rounded-lg`;
const ClosedInfo = tw.div`flex flex-wrap gap-3 mt-3 p-2 bg-green-50 rounded-lg`;
const InfoChip = tw.div`flex flex-col`;
const LostInfo = tw.div`mt-3 p-2 bg-red-50 rounded-lg`;
const LostReason = tw.p`text-sm text-gray-700 mt-1`;
const Footer = tw.div`flex justify-end mt-1 pt-2`;
const EmptyState = tw.div`text-center text-gray-500 text-sm py-10`;
const ContentBox = tw.div`flex justify-between items-center bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 mt-3`;
// const FabButton = tw.button`fixed bottom-6 right-6 w-14 h-14 text-white rounded-full flex items-center justify-center shadow-md bg-gradient-to-r from-primary-color to-teal-400 `;
const FabButton = tw.button`fixed bottom-6 right-6 w-14 h-14 text-white rounded-full flex items-center justify-center shadow-md bg-gradient-to-r from-primary-color to-teal-400 `;

export default ViewDealsModal;